import type MedicationRequest from "parog-web/models/prescriptions/medication-request";

export type ATCOrMedicationName = string;

export class Dose {
  period: string | undefined;
  quantity: number;
  unit: string | undefined;

  constructor(opts: {
    quantity: number | string;
    unit: string | undefined;
    period: string | undefined;
  }) {
    this.quantity = +opts.quantity;
    this.unit = opts.unit;
    this.period = opts.period;
  }

  isEqual(dose: Dose | undefined): boolean {
    return (
      this.quantity === dose?.quantity &&
      this.unit === dose?.unit &&
      this.period === dose?.period
    );
  }
}

export interface ATCGroup {
  // The last encountered MedicationRequest of the prescription for the given ATC.
  medicationRequest: MedicationRequest;

  // Concatenated comments of all MedicationRequest with same ATC or name.
  comment: string;

  // The total dose for this prescription, or undefined if it cannot be computed.
  doseDisplayed: Dose | undefined;
}

/**
 * Grouped a list of MedicationRequest by ATC code
 * and compute the total dosage for each group if the dosage is the same for a given ATC code.
 */
export default function medicationRequestGroupByAtc(
  medicationRequests: MedicationRequest[]
): Map<ATCOrMedicationName, ATCGroup> {
  const map = new Map<ATCOrMedicationName, ATCGroup>();

  medicationRequests.forEach(medicationRequest => {
    const key =
      medicationRequest.medication?.molecule?.code ||
      medicationRequest.medication?.name;
    if (!key) return;

    const existingGroup = map.get(key);

    let doseDisplayed: Dose | undefined;

    if (medicationRequest.dosePerTimingUnit) {
      doseDisplayed = new Dose({
        quantity: medicationRequest.dosePerTimingUnit,
        unit: medicationRequest.medication?.unit,
        period: medicationRequest.dosage?.timing?.periodUnit
      });
    }

    let comment = "";

    //if two medications have the same molecule and the same timing
    //the dosage is the sum of them
    //else it cannot be computed.

    if (existingGroup) {
      if (
        doseDisplayed &&
        // If doseDisplayed is set, the medicationRequest has a dosage
        existingGroup.medicationRequest.dosage?.isEqual(
          medicationRequest.dosage
        )
      ) {
        if (existingGroup.doseDisplayed != undefined) {
          doseDisplayed.quantity += existingGroup.doseDisplayed.quantity;
        }
      } else {
        doseDisplayed = undefined;
      }

      comment = existingGroup.comment || "";
    }

    // Concatenate comments of all same ATC code
    comment += medicationRequest.comment ? " " + medicationRequest.comment : "";

    map.set(key, {
      medicationRequest,
      doseDisplayed,
      comment
    });
  });

  return map;
}
